/**
 * Post-authentication redirect sanitising.
 *
 * A `?redirect=` value is attacker-controllable (it travels in a link), so it
 * may only ever address somewhere inside this app. Anything that could leave
 * the origin — an absolute URL, a protocol-relative `//evil.example`, or a
 * `javascript:` payload — is discarded rather than followed.
 *
 * Shared by /auth and /post-auth so the two cannot drift: /auth accepted the
 * param, forwarded it to /post-auth, and /post-auth used to ignore it entirely,
 * which is how a client sent to sign in from the booking payment gate ended up
 * on a dashboard with their booking abandoned.
 */

/**
 * Returns the value only when it is a safe same-origin path, else null.
 *
 * `/` returns null: it carries no intent, so callers should fall back to their
 * own default (role-based routing) rather than pinning the user to the home page.
 */
export function safeInternalRedirect(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const value = raw.trim();
  // Must be rooted, and must not be protocol-relative ("//host" navigates off-origin).
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  // Backslashes are normalised to "/" by some browsers, so "/\evil.example"
  // can behave like a protocol-relative URL.
  if (value.startsWith('/\\')) return null;
  if (value === '/') return null;
  return value;
}

/**
 * Where sign-out lands: /auth, carrying a same-origin path to come back to
 * after the next sign-in (anything else is dropped).
 */
export function signedOutPage(returnTo?: string | null): string {
  const target = safeInternalRedirect(returnTo);
  return target ? `/auth?redirect=${encodeURIComponent(target)}` : '/auth';
}
